/* UI-DESIGN: /preview is the design QA dashboard — a single page that lists
   every preview/test URL plus a live theme switcher (White Lotus ↔ Sherlock)
   so we can validate the theme contract without opening DevTools. Frontend-only;
   delete when the design system is fully shipped. */
import { useSyncExternalStore } from "react";
import clsx from "clsx";
import { Link, useSearchParams } from "react-router-dom";
import CaseMark from "../../components/icons/CaseMark";
import styles from "./Preview.module.scss";
import PhoneConcepts from "./phone/PhoneConcepts";

type RouteStatus = "designed" | "preview" | "pending" | "deprecated";

interface PreviewRoute {
  url: string;
  label: string;
  description: string;
  status: RouteStatus;
  external?: boolean;
}

/* UI-DESIGN: section 1 — routes that render against sample data so designs
   are reviewable without a real backend */
const PREVIEW_ROUTES: PreviewRoute[] = [
  {
    url: "/passport?preview=sample",
    label: "Passport Dossier",
    description: "Biometric passport + tabs (Character / Case Log / Relationships)",
    status: "preview",
  },
  {
    url: "/game/sample-in-progress",
    label: "Game In Progress",
    description: "Dossier reveal — interrogation spotlight, passport-photo avatar, fellow suspects",
    status: "preview",
  },
  {
    url: "/game/sample-host",
    label: "Game — Host view",
    description: "Event details + guest list, host actions, stats card",
    status: "preview",
  },
  {
    url: "/game/sample-guest",
    label: "Game — Guest view",
    description: "Event details + RSVP CTA, awaiting character card",
    status: "preview",
  },
];

/* UI-DESIGN: section 2 — pages whose live production view IS the design preview */
const DESIGNED_ROUTES: PreviewRoute[] = [
  { url: "/", label: "Home", description: "Hero collage, draggable photos, trail draw-in, plan-a-mystery", status: "designed" },
  { url: "/mysteries", label: "Mysteries", description: "Featured spotlight + grid + CTA band", status: "designed" },
  { url: "/open-cases", label: "Open Cases", description: "The user's registered mysteries (needs login)", status: "designed" },
  { url: "/about", label: "How it works", description: "3-step dossier walkthrough + 'On the night' card", status: "designed" },
  { url: "/create-game", label: "Create Game", description: "Two numbered steps + case picker", status: "designed" },
  { url: "/register", label: "Register", description: "Sealed envelope → 'Count me in' form", status: "designed" },
  { url: "/login", label: "Login", description: "Authorized Access side door — OTP fallback", status: "designed" },
];

/* UI-DESIGN: section 3 — what's left to design or to remove */
const PENDING_ROUTES: PreviewRoute[] = [
  { url: "/admin-games", label: "Admin Games", description: "Internal admin list — engineer territory", status: "pending" },
  { url: "/lobby", label: "Lobby (deprecated)", description: "Superseded by /game/:gameId — engineer should delete route", status: "deprecated" },
  { url: "/host", label: "Host (deprecated)", description: "Local-state demo — predates real game flow, engineer should delete", status: "deprecated" },
];

const STATUS_LABEL: Record<RouteStatus, string> = {
  designed: "Designed",
  preview: "Preview",
  pending: "Pending",
  deprecated: "Deprecated",
};

const STATUS_CLASS: Record<RouteStatus, string> = {
  designed: styles.statusDone,
  preview: styles.statusPreview,
  pending: styles.statusPending,
  deprecated: styles.statusDeprecated,
};

const RouteCard = ({ route, dim = false }: { route: PreviewRoute; dim?: boolean }) => (
  <Link
    to={route.url}
    className={clsx(styles.route, dim && styles.routeDim)}
  >
    <div className={styles.routeTopRow}>
      <span className={styles.routeUrl}>{route.url}</span>
      <span className={clsx(styles.routeStatus, STATUS_CLASS[route.status])}>
        {STATUS_LABEL[route.status]}
      </span>
      <span className={styles.routeArrow} aria-hidden>→</span>
    </div>
    <p className={styles.routeLabel}>{route.label}</p>
    <p className={styles.routeDescription}>{route.description}</p>
  </Link>
);

/* UI-DESIGN: subscribe to data-puzzle-theme on <html> so the switcher
   shows the live active theme even if it was set in DevTools */
const subscribePuzzleTheme = (callback: () => void) => {
  if (typeof window === "undefined") return () => undefined;
  const observer = new MutationObserver(callback);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-puzzle-theme"],
  });
  return () => observer.disconnect();
};

const getPuzzleThemeSnapshot = () => {
  if (typeof document === "undefined") return "white-lotus";
  return document.documentElement.dataset.puzzleTheme || "white-lotus";
};

const PUZZLE_THEMES = [
  { id: "white-lotus", label: "White Lotus" },
  { id: "sherlock", label: "Sherlock" },
] as const;

/* UI-DESIGN: LB-72 /preview?phone is the phone concept sheet; App.tsx
   (routes) is off limits, so it hangs off this review page */
const Preview = () => {
  const [params] = useSearchParams();
  const phone = params.get("phone");
  return phone === null ? <PreviewDashboard /> : <PhoneConcepts direction={phone} />;
};

const PreviewDashboard = () => {
  const activeTheme = useSyncExternalStore(
    subscribePuzzleTheme,
    getPuzzleThemeSnapshot,
    () => "white-lotus"
  );

  const setTheme = (themeId: string) => {
    if (themeId === "white-lotus") {
      delete document.documentElement.dataset.puzzleTheme;
    } else {
      document.documentElement.dataset.puzzleTheme = themeId;
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.inner}>
        <header className={clsx(styles.header, "ui-reveal")}>
          <div className={styles.headerCopy}>
            <p className={styles.kicker}>
              <CaseMark className={styles.kickerMark} />
              Design Index
            </p>
            <h1 className={styles.title}>Preview &amp; QA</h1>
            <p className={styles.subtitle}>
              Every preview/test URL in one place, plus a live theme switcher
              so you can validate the theme contract without opening DevTools.
            </p>
          </div>

          {/* UI-DESIGN: pill toggle that flips data-puzzle-theme live */}
          <div className={styles.themeSwitcher}>
            <span className={styles.themeLabel}>Puzzle Theme</span>
            <div className={styles.themeOptions} role="radiogroup" aria-label="Puzzle theme">
              {PUZZLE_THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="radio"
                  aria-checked={activeTheme === t.id}
                  onClick={() => setTheme(t.id)}
                  className={clsx(
                    styles.themeButton,
                    activeTheme === t.id && styles.themeButtonActive
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </header>

        <section className={clsx(styles.section, "ui-reveal", "ui-reveal-1")}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>01</span>
            <h2 className={styles.sectionTitle}>Sample-data previews</h2>
            <span className={styles.sectionCount}>{PREVIEW_ROUTES.length} routes</span>
          </div>
          <div className={styles.grid}>
            {PREVIEW_ROUTES.map((r) => (
              <RouteCard key={r.url} route={r} />
            ))}
          </div>
        </section>

        <section className={clsx(styles.section, "ui-reveal", "ui-reveal-2")}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>02</span>
            <h2 className={styles.sectionTitle}>Production pages (designed)</h2>
            <span className={styles.sectionCount}>{DESIGNED_ROUTES.length} routes</span>
          </div>
          <div className={styles.grid}>
            {DESIGNED_ROUTES.map((r) => (
              <RouteCard key={r.url} route={r} />
            ))}
          </div>
        </section>

        <section className={clsx(styles.section, "ui-reveal", "ui-reveal-3")}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionNumber}>03</span>
            <h2 className={styles.sectionTitle}>Pending &amp; deprecated</h2>
            <span className={styles.sectionCount}>{PENDING_ROUTES.length} routes</span>
          </div>
          <div className={styles.grid}>
            {PENDING_ROUTES.map((r) => (
              <RouteCard key={r.url} route={r} dim />
            ))}
          </div>
        </section>

        <p className={styles.footnote}>
          Theme switcher writes <code>data-puzzle-theme</code> to{" "}
          <code>&lt;html&gt;</code>; every component reading from{" "}
          <code>--case-*</code> tokens reskins instantly. Sample-data preview
          URLs use <code>SAMPLE_GAMES</code> and <code>SAMPLE_PLAYER</code>{" "}
          fallbacks — delete those when the backend ships real test data.
        </p>
      </div>
    </div>
  );
};

export default Preview;
